import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import type { ReactNode } from "react";
import type { MediaItem, SourceConfig } from "@gooner/shared";
import { api, userApi } from "../../lib/api";
import { publicSettings } from "../../lib/publicSettings";
import { reportClipView } from "../../lib/clipViews";
import { useSession } from "../../lib/session";
import { shuffle } from "../../lib/shuffle";
import { cx } from "../ui/primitives";
import { Spinner } from "../ui/Spinner";
import { MediaSurface } from "../ui/MediaSurface";
import { IconClose, IconMute, IconNext, IconPlay, IconVolume, IconWarning } from "../ui/icons";
import { FapBar } from "./FapBar";
import { ClipEditModal } from "./ClipEditModal";

/**
 * "Goon fuel" — a small floating video panel pinned to the bottom-right of the
 * control screens (mounted from ControlApp, so it never appears on a display).
 * It autonomously loops our OWN self-hosted catalog while you navigate; the little ×
 * collapses it to a re-openable pill. Every clip streams from `/api/media/<key>` (the
 * fail-closed `random` catalog source) — never an external feed.
 */

/** Default source: a shuffled draw from our self-hosted catalog (fail-closed —
 *  only on-our-servers media, never an external RedGifs/Reddit feed). */
const DEFAULT_FUEL_SOURCE: SourceConfig = { kind: "random" };
/** Prefetch the next page once we're within this many clips of the buffer's end. */
const PREFETCH_AHEAD = 4;
/** Safety cap: force-advance if a clip's `ended` never fires (stall/loop guard). */
const MAX_CLIP_MS = 30_000;
/** Backoff before retrying after the feed fails with nothing buffered. */
const RETRY_MS = 20_000;

type Status = "loading" | "ready" | "error";

/** One choice in the widget's source dropdown. */
interface FuelOption {
  key: string;
  label: string;
  group: string;
}
/** Remembers the viewer's last Goon Fuel choice (per browser). */
const FUEL_SELECT_KEY = "gooner.goonFuel.selected";
/** Remembers whether the viewer left the panel open or collapsed (per browser). */
const FUEL_OPEN_KEY = "gooner.goonFuel.open";

/** Open unless this browser's viewer collapsed it. Storage unavailable: open. */
function readFuelOpen(): boolean {
  try {
    return localStorage.getItem(FUEL_OPEN_KEY) !== "0";
  } catch {
    return true;
  }
}

/** Resolve a dropdown key to a real source (a playlist id, or the special keys). */
function sourceForKey(key: string, fallback: SourceConfig): SourceConfig {
  // "all" is the self-hosted catalog shuffle; "trending" kept for back-compat with a
  // previously-saved selection (old external default) — both resolve to our catalog now.
  if (key === "all" || key === "trending") return DEFAULT_FUEL_SOURCE;
  if (key === "default") return fallback;
  return { kind: "playlist", playlistId: key };
}

/**
 * The width at which ControlApp shows Fuel at all: its wrapper is `hidden lg:block`, and
 * Tailwind's `lg` is 1024px. Matched here because CSS can only hide the panel; it cannot
 * stop a <video> inside it. Hidden by `display: none`, the player used to keep going:
 * narrow an unmuted panel past 1024px and it played on, audible, advancing every 30s,
 * with its Mute and × hidden along with it; on a phone /how opened it unseen and
 * downloaded a fresh clip every 30s.
 */
export const FUEL_MIN_WIDTH_PX = 1024;
const FUEL_MEDIA_QUERY = `(min-width: ${FUEL_MIN_WIDTH_PX}px)`;

/** True when the viewport is wide enough for the panel to be visible. False with no
 *  `matchMedia` (a server render, a check's bare harness): no media without a screen. */
function fuelViewportWide(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia(FUEL_MEDIA_QUERY).matches
  );
}

function subscribeFuelViewport(onChange: () => void): () => void {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return () => undefined;
  const mq = window.matchMedia(FUEL_MEDIA_QUERY);
  mq.addEventListener("change", onChange);
  return () => mq.removeEventListener("change", onChange);
}

/**
 * Fuel exists only while it can be seen. Below 1024px nothing under here mounts: no
 * settings read, no feed fetch, no <video>. Crossing below unmounts the player, and
 * MediaSurface's own teardown pauses the element, clears its src and calls load(), which
 * is what actually stops playback and the download. Crossing back mounts it fresh (muted).
 * The picked source and the open state live in localStorage, so unmounting loses neither.
 */
export function GoonFuel() {
  const wide = useSyncExternalStore(subscribeFuelViewport, fuelViewportWide, fuelViewportWide);
  if (!wide) return null;
  return <GoonFuelWhenVisible />;
}

function GoonFuelWhenVisible() {
  const [config, setConfig] = useState<{ enabled: boolean; source?: SourceConfig } | null>(null);
  useEffect(() => {
    let cancelled = false;
    // publicSettings falls back to DEFAULT_SETTINGS on failure; goonFuel.enabled is
    // true there, so this stays fail-open without a redundant .catch.
    void publicSettings().then((s) => !cancelled && setConfig(s.goonFuel));
    return () => {
      cancelled = true;
    };
  }, []);
  // Nothing until we know; nothing at all when disabled (media hooks never mount).
  if (!config?.enabled) return null;
  return <GoonFuelShell defaultSource={config.source ?? DEFAULT_FUEL_SOURCE} />;
}

/**
 * Owns the source picker: lists the Editor's Choice collections + the signed-in
 * user's playlists and lets the viewer switch what the widget plays at runtime.
 */
function GoonFuelShell({ defaultSource }: { defaultSource: SourceConfig }) {
  const user = useSession((s) => s.user);
  const [featured, setFeatured] = useState<FuelOption[]>([]);
  const [mine, setMine] = useState<FuelOption[]>([]);
  const [selectedKey, setSelectedKey] = useState<string>(() => {
    const saved = (typeof localStorage !== "undefined" && localStorage.getItem(FUEL_SELECT_KEY)) || "";
    if (saved) return saved;
    return defaultSource.kind === "playlist" ? defaultSource.playlistId : "all";
  });

  useEffect(() => {
    let cancelled = false;
    api
      .listCollections()
      .then((r) => {
        if (cancelled) return;
        setFeatured(
          r.collections
            .filter((c) => c.editorsChoice)
            .map((c) => ({ key: c.id, label: c.name, group: "Editor's Choice" })),
        );
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!user) {
      setMine([]);
      return;
    }
    let cancelled = false;
    userApi
      .listPlaylists()
      .then((r) => {
        if (cancelled) return;
        setMine(r.playlists.map((p) => ({ key: p.id, label: p.name, group: "Your collections" })));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [user]);

  const options: FuelOption[] = [...featured, ...mine, { key: "all", label: "All clips", group: "Other" }];
  // Guarantee the current selection is always a visible option, even mid-load.
  if (!options.some((o) => o.key === selectedKey)) {
    options.unshift({ key: selectedKey, label: "Selected collection", group: "Editor's Choice" });
  }

  function select(key: string) {
    setSelectedKey(key);
    try {
      localStorage.setItem(FUEL_SELECT_KEY, key);
    } catch {
      /* storage unavailable */
    }
  }

  return (
    <GoonFuelPlayer
      source={sourceForKey(selectedKey, defaultSource)}
      options={options}
      selectedKey={selectedKey}
      onSelect={select}
    />
  );
}

function GoonFuelPlayer({
  source,
  options,
  selectedKey,
  onSelect,
}: {
  source: SourceConfig;
  options: FuelOption[];
  selectedKey: string;
  onSelect: (key: string) => void;
}) {
  // Starts open, and after that it stays as the viewer last left it, across pages and
  // reloads. Below `lg` none of this mounts (see GoonFuel above). 2026-09-27 (#8) made it
  // start as the pill because the panel sits over the page's tiles; reversed on the
  // owner's call 2026-11-01, the × being one click and now remembered.
  const [open, setOpenState] = useState(readFuelOpen);
  const setOpen = useCallback((next: boolean) => {
    setOpenState(next);
    try {
      localStorage.setItem(FUEL_OPEN_KEY, next ? "1" : "0");
    } catch {
      /* storage unavailable */
    }
  }, []);
  // Read by show(), which is built once per source and would otherwise see a stale `open`.
  const openRef = useRef(open);
  openRef.current = open;
  const [muted, setMuted] = useState(true);
  const [status, setStatus] = useState<Status>("loading");
  const [current, setCurrent] = useState<MediaItem | null>(null);
  // A snapshot of the clip to show in the detail panel (double-click the video).
  // Captured on open so the feed advancing behind it doesn't swap the panel's clip.
  const [preview, setPreview] = useState<MediaItem | null>(null);
  // The detail panel belongs to the open player. Collapsed, the panel renders only the
  // pill, so a preview left set would sit unseen and come back over the page the next
  // time the pill opened Fuel — Details reappearing "instead of Fuel", for a clip from
  // minutes ago. Cleared on every collapse, whoever collapsed it (the ×, the route).
  useEffect(() => {
    if (!open) setPreview(null);
  }, [open]);

  // Live playback buffer, kept in refs to dodge stale closures in the timers.
  const itemsRef = useRef<MediaItem[]>([]);
  const indexRef = useRef(0);
  const cursorRef = useRef<string | undefined>(undefined);
  const hasMoreRef = useRef(true);
  const fetchingRef = useRef(false);
  const mountedRef = useRef(true);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Watch tracking: measure how long each clip is on screen and report it (per
  // clip) for the signed-in user's stats. Wall-clock between clip changes.
  const watchClipRef = useRef<MediaItem | null>(null);
  const watchStartRef = useRef(0);

  const flushWatch = useCallback(() => {
    const clip = watchClipRef.current;
    watchClipRef.current = null;
    if (!clip) return;
    const now = Date.now();
    const seconds = (now - watchStartRef.current) / 1000;
    if (seconds >= 0.5 && useSession.getState().user) {
      // The single stream ends at the flush, so its end IS now — said explicitly, so
      // this row places itself the same way a screen's batched intervals do.
      void userApi.recordWatch(clip, seconds, now).catch(() => undefined);
    }
  }, []);

  const beginWatch = useCallback(
    (item: MediaItem | null) => {
      flushWatch();
      if (item) {
        // A view, signed in or not — once per clip per page (lib/clipViews.ts). The watch
        // TIME below is the signed-in stats' business and stays gated on a user.
        reportClipView(item);
        watchClipRef.current = item;
        watchStartRef.current = Date.now();
      }
    },
    [flushWatch],
  );

  // Drag: `pos` is null while pinned bottom-right; once dragged it holds an
  // absolute top-left in px. `grab` is the pointer offset within the panel.
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const grab = useRef<{ dx: number; dy: number } | null>(null);

  /** Pull the next feed page and append it; guarded against overlapping calls. */
  const fetchMore = useCallback(async () => {
    if (fetchingRef.current || !hasMoreRef.current) return;
    fetchingRef.current = true;
    try {
      const res = await api.fetchSource(source, cursorRef.current);
      if (!mountedRef.current) return;
      // Videos only — this is fuel, not a slideshow. Shuffle per instance so the
      // widget doesn't mirror another player showing the same feed's item 0.
      const vids = shuffle(res.items.filter((m) => m.kind === "video"));
      itemsRef.current = itemsRef.current.concat(vids);
      cursorRef.current = res.nextCursor;
      hasMoreRef.current = Boolean(res.nextCursor);
    } catch {
      if (!mountedRef.current) return;
      if (itemsRef.current.length === 0) setStatus("error");
    } finally {
      fetchingRef.current = false;
    }
  }, [source]);

  /** Show a buffer index, topping up the buffer as we near its tail. */
  const show = useCallback(
    (idx: number) => {
      const item = itemsRef.current[idx];
      if (!item) return;
      // Start timing this clip (banks the previous one), but only while the panel is
      // open. Collapsed, nothing is on screen: the feed still loads behind the pill, and
      // the open effect below begins the watch (and reports the view) for whatever is
      // current when the viewer opens it. Now that the pill is the default, without this
      // gate every page load would count a view and bank its whole stay as watch time
      // for a clip nobody saw.
      if (openRef.current) beginWatch(item);
      indexRef.current = idx;
      setCurrent(item);
      setStatus("ready");
      if (hasMoreRef.current && itemsRef.current.length - idx <= PREFETCH_AHEAD) {
        void fetchMore();
      }
    },
    [fetchMore, beginWatch],
  );

  const advance = useCallback(() => {
    const buf = itemsRef.current;
    if (buf.length === 0) return;
    const next = (indexRef.current + 1) % buf.length;
    // Reached the end of the buffer — reshuffle so each pass is a fresh random
    // order (matters most for a finite playlist/collection source that loops).
    if (next === 0 && buf.length > 1) itemsRef.current = shuffle(buf);
    show(next);
  }, [show]);

  // Initial load — also re-runs when the source changes (fetchMore/show are
  // rebuilt from `source`), so switching playlists resets the buffer and reloads
  // in place without remounting (the widget keeps its dragged position).
  useEffect(() => {
    mountedRef.current = true;
    // Fresh start for this source.
    itemsRef.current = [];
    indexRef.current = 0;
    cursorRef.current = undefined;
    hasMoreRef.current = true;
    fetchingRef.current = false;
    setCurrent(null);
    setStatus("loading");
    void (async () => {
      await fetchMore();
      if (!mountedRef.current) return;
      if (itemsRef.current.length > 0) show(0);
      else setStatus("error");
    })();
    return () => {
      mountedRef.current = false;
      flushWatch(); // bank the final clip's watch time on unmount
    };
  }, [fetchMore, show, flushWatch]);

  // Pause/resume watch timing with the panel's open state (don't count time
  // while collapsed to the pill).
  useEffect(() => {
    if (open) beginWatch(itemsRef.current[indexRef.current] ?? null);
    else flushWatch();
  }, [open, beginWatch, flushWatch]);

  // Auto-retry the feed when we're stuck with nothing to play.
  useEffect(() => {
    if (status !== "error" || !open) return;
    const t = setTimeout(() => {
      hasMoreRef.current = true;
      cursorRef.current = undefined;
      setStatus("loading");
      void (async () => {
        await fetchMore();
        if (!mountedRef.current) return;
        if (itemsRef.current.length > 0) show(0);
        else setStatus("error");
      })();
    }, RETRY_MS);
    return () => clearTimeout(t);
  }, [status, open, fetchMore, show]);

  // Watchdog: advance if the current clip stalls or never signals `ended`.
  useEffect(() => {
    if (!open || !current) return;
    const t = setTimeout(advance, MAX_CLIP_MS);
    return () => clearTimeout(t);
  }, [open, current, advance]);

  // Keep the <video> element's muted *property* in sync (React sets it unreliably).
  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = muted;
  }, [muted, current]);

  // Begin a drag from the header (but not when a control button was pressed).
  function onDragStart(e: React.PointerEvent) {
    if ((e.target as HTMLElement).closest("button")) return;
    const el = rootRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    grab.current = { dx: e.clientX - r.left, dy: e.clientY - r.top };
    setPos({ x: r.left, y: r.top }); // switch from bottom/right anchor to px
    e.currentTarget.setPointerCapture(e.pointerId);
  }

  function onDragMove(e: React.PointerEvent) {
    const g = grab.current;
    const el = rootRef.current;
    if (!g || !el) return;
    const m = 8; // keep at least this many px on-screen
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const x = Math.min(Math.max(e.clientX - g.dx, m), window.innerWidth - w - m);
    const y = Math.min(Math.max(e.clientY - g.dy, m), window.innerHeight - h - m);
    setPos({ x, y });
  }

  function onDragEnd(e: React.PointerEvent) {
    grab.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* pointer already released */
    }
  }

  // When dragged, anchor by absolute top-left; otherwise pinned bottom-right.
  const anchorClass = pos ? "" : "bottom-4 right-4";
  const anchorStyle = pos ? { left: pos.x, top: pos.y } : undefined;

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={anchorStyle}
        className={cx(
          "fixed z-40 inline-flex items-center gap-1.5 rounded-full",
          anchorClass,
          "border border-[var(--color-edge)] bg-[var(--color-panel)]/90 px-3 py-2 shadow-xl backdrop-blur",
          "text-[10px] font-bold uppercase tracking-[0.18em] text-[var(--color-accent-soft)]",
          "hover:bg-[var(--color-panel-2)]",
        )}
        title="Open goon fuel"
      >
        <IconPlay width={13} height={13} />
        Goon Fuel
      </button>
    );
  }

  return (
    <div
      ref={rootRef}
      style={anchorStyle}
      className={cx(
        "fixed z-40 w-64 overflow-hidden rounded-xl",
        anchorClass,
        "border border-[var(--color-edge)] bg-[var(--color-panel)] shadow-2xl",
        "ring-1 ring-[var(--color-accent)]/20",
      )}
    >
      {/* header / control bar — also the drag handle */}
      <div
        onPointerDown={onDragStart}
        onPointerMove={onDragMove}
        onPointerUp={onDragEnd}
        className="flex touch-none cursor-move select-none items-center gap-2 border-b border-[var(--color-edge)] px-2.5 py-1.5"
      >
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--color-accent)] opacity-60" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-[var(--color-accent)]" />
        </span>
        <span className="select-none whitespace-nowrap text-[10px] font-bold uppercase tracking-[0.18em] text-[var(--color-accent-soft)]">
          Goon Fuel
        </span>
        <SourceMenu options={options} selectedKey={selectedKey} onSelect={onSelect} />
        <span className="flex-1" />
        <IconBtn label={muted ? "Unmute" : "Mute"} onClick={() => setMuted((m) => !m)}>
          {muted ? <IconMute width={15} height={15} /> : <IconVolume width={15} height={15} />}
        </IconBtn>
        <IconBtn label="Skip" onClick={advance}>
          <IconNext width={15} height={15} />
        </IconBtn>
        <IconBtn label="Close goon fuel" onClick={() => setOpen(false)}>
          <IconClose width={15} height={15} />
        </IconBtn>
      </div>

      {/* video stage — double-click opens the clip's detail panel */}
      <div
        className="relative aspect-[3/4] w-full bg-black"
        onDoubleClick={() => current && setPreview(current)}
        title="Double-click for details"
      >
        {current && (
          <MediaSurface
            key={current.url}
            ref={videoRef}
            kind="video"
            src={current.url}
            poster={current.posterUrl}
            muted={muted}
            onEnded={advance}
            onError={advance}
            onAutoMuted={() => setMuted(true)}
          />
        )}

        {status === "loading" && (
          <Overlay>
            <Spinner />
          </Overlay>
        )}
        {status === "error" && (
          <Overlay>
            <IconWarning className="mb-1 text-amber-400" width={18} height={18} />
            <span className="text-[10px] tracking-wide text-zinc-400">
              Fuel offline — retrying…
            </span>
          </Overlay>
        )}

        {/* TikTok-style fap overlay for the clip currently playing */}
        {current && <FapBar clip={current} />}
      </div>

      {preview && <ClipEditModal clip={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

/** Compact chevron button in the header that reveals the source list as a popover. */
function SourceMenu({
  options,
  selectedKey,
  onSelect,
}: {
  options: FuelOption[];
  selectedKey: string;
  onSelect: (key: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const groups = [...new Set(options.map((o) => o.group))];

  return (
    <div ref={ref} className="relative" onPointerDown={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Choose what Goon Fuel plays"
        aria-label="Choose source"
        className={cx(
          "flex h-5 w-5 items-center justify-center rounded text-zinc-400 transition-colors hover:bg-[var(--color-panel-2)] hover:text-zinc-100",
          open && "bg-[var(--color-panel-2)] text-zinc-100",
        )}
      >
        <svg
          width="11"
          height="11"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          className={cx("transition-transform", open && "rotate-180")}
          aria-hidden="true"
        >
          <path d="M3 4.5 L6 7.5 L9 4.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="absolute left-0 top-6 z-50 max-h-64 w-52 overflow-auto rounded-lg border border-[var(--color-edge)] bg-[var(--color-panel)] py-1 shadow-2xl">
          {groups.map((g) => (
            <div key={g}>
              <div className="px-2.5 pb-0.5 pt-1.5 text-[9px] font-bold uppercase tracking-wider text-zinc-500">
                {g}
              </div>
              {options
                .filter((o) => o.group === g)
                .map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => {
                      onSelect(o.key);
                      setOpen(false);
                    }}
                    className={cx(
                      "block w-full truncate px-2.5 py-1.5 text-left text-[11px] transition-colors",
                      o.key === selectedKey
                        ? "bg-[var(--color-panel-2)] text-[var(--color-accent-soft)]"
                        : "text-zinc-300 hover:bg-[var(--color-panel-2)]",
                    )}
                  >
                    {o.label}
                  </button>
                ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function IconBtn({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className="rounded p-1 text-zinc-400 transition-colors hover:bg-[var(--color-panel-2)] hover:text-white"
    >
      {children}
    </button>
  );
}

function Overlay({ children }: { children: ReactNode }) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/40 px-3 text-center">
      {children}
    </div>
  );
}
